import React, { useRef, useState } from 'react';
import { Alert, Button, Form, Modal, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SettingsIcon } from '../../../../../assets/icons/SettingsIcon';
import {
  partialUnscheduleFromLayoutAction,
  PartialUnscheduleFromLayoutPayload,
  removeSchedule,
} from '../../../../../features/schedule/scheduleActions';
import {
  selectScheduleDayOptions,
  selectScheduleTournamentClasses,
  selectScheduleUpdating,
} from '../../../../../features/schedule/scheduleSelectors';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { ScheduleToolbarButton } from './ScheduleToolbarButton';

type PendingAction = 'removeAll' | 'partialClass' | 'partialDay';
type ClassDayScope = 'all' | 'selected';

const prefix = 'creativeDetail-';

const uniqueNumbers = (values: number[]) => Array.from(new Set(values));

/** Toolbar trigger only; modal body mounts when open (avoids schedule-slice subscriptions while closed). */
export const ScheduleLayoutSettingsModal = React.memo(function ScheduleLayoutSettingsModal() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  return (
    <>
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip id="schedule-settings-tip">{t(prefix + 'scheduleSettingsButton')}</Tooltip>
        }
      >
        <ScheduleToolbarButton
          aria-label={t(prefix + 'scheduleSettingsButton')}
          onClick={() => setShow(true)}
          icon={<SettingsIcon w="18" h="18" />}
          labelClassName="d-none d-lg-inline small"
        >
          {t(prefix + 'scheduleSettingsButton')}
        </ScheduleToolbarButton>
      </OverlayTrigger>

      {show ? <ScheduleLayoutSettingsModalBody onClose={() => setShow(false)} /> : null}
    </>
  );
});

type ScheduleLayoutSettingsModalBodyProps = {
  onClose: () => void;
};

const ScheduleLayoutSettingsModalBody = ({ onClose }: ScheduleLayoutSettingsModalBodyProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const dayOptions = useAppSelector(selectScheduleDayOptions);
  const classOptions = useAppSelector(selectScheduleTournamentClasses);
  const updatingSchedule = useAppSelector(selectScheduleUpdating);

  const [confirmText, setConfirmText] = useState('');
  const [selectedClassIds, setSelectedClassIds] = useState<number[]>([]);
  const [classDayScope, setClassDayScope] = useState<ClassDayScope>('all');
  const [classDayKeys, setClassDayKeys] = useState<number[]>([]);
  const [clearDayKeys, setClearDayKeys] = useState<number[]>([]);
  const [classIncludeStartedAndFinished, setClassIncludeStartedAndFinished] = useState(false);
  const [dayIncludeStartedAndFinished, setDayIncludeStartedAndFinished] = useState(false);
  const [classAcknowledged, setClassAcknowledged] = useState(false);
  const [dayAcknowledged, setDayAcknowledged] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const submitLockRef = useRef(false);

  const confirmWord = t(prefix + 'scheduleRemoveConfirmWord');
  const isBusy = pendingAction !== null || updatingSchedule;

  const resetLocalState = () => {
    setConfirmText('');
    setSelectedClassIds([]);
    setClassDayScope('all');
    setClassDayKeys([]);
    setClearDayKeys([]);
    setClassIncludeStartedAndFinished(false);
    setDayIncludeStartedAndFinished(false);
    setClassAcknowledged(false);
    setDayAcknowledged(false);
    setPendingAction(null);
    submitLockRef.current = false;
  };

  const handleClose = () => {
    if (pendingAction) return;
    resetLocalState();
    onClose();
  };

  const withSubmitLock = async (action: PendingAction, cb: () => Promise<boolean>) => {
    if (submitLockRef.current || updatingSchedule) return;
    submitLockRef.current = true;
    setPendingAction(action);

    try {
      const succeeded = await cb();
      if (succeeded) {
        resetLocalState();
        onClose();
      }
    } finally {
      submitLockRef.current = false;
      setPendingAction(null);
    }
  };

  const toggleClassId = (classId: number) => {
    setSelectedClassIds((current) =>
      current.includes(classId)
        ? current.filter((id) => id !== classId)
        : uniqueNumbers([...current, classId])
    );
    setClassAcknowledged(false);
  };

  const toggleClassDayKey = (dayKey: number) => {
    setClassDayKeys((current) =>
      current.includes(dayKey)
        ? current.filter((key) => key !== dayKey)
        : uniqueNumbers([...current, dayKey])
    );
    setClassAcknowledged(false);
  };

  const toggleClearDayKey = (dayKey: number) => {
    setClearDayKeys((current) =>
      current.includes(dayKey)
        ? current.filter((key) => key !== dayKey)
        : uniqueNumbers([...current, dayKey])
    );
    setDayAcknowledged(false);
  };

  const handleClassDayScopeChange = (scope: ClassDayScope) => {
    setClassDayScope(scope);
    setClassAcknowledged(false);
    if (scope === 'all') {
      setClassDayKeys([]);
    }
  };

  const handleRemoveAll = () => {
    withSubmitLock('removeAll', () => dispatch(removeSchedule(t)));
  };

  const handleUnscheduleClasses = () => {
    const payload: PartialUnscheduleFromLayoutPayload = {
      mode: 'byClass',
      classIds: selectedClassIds,
      dayScope: classDayScope,
      dayKeys: classDayScope === 'all' ? [] : classDayKeys,
      includeStartedAndFinished: classIncludeStartedAndFinished,
    };
    withSubmitLock('partialClass', () => dispatch(partialUnscheduleFromLayoutAction(payload, t)));
  };

  const handleClearDays = () => {
    const payload: PartialUnscheduleFromLayoutPayload = {
      mode: 'byDay',
      dayKeys: clearDayKeys,
      includeStartedAndFinished: dayIncludeStartedAndFinished,
    };
    withSubmitLock('partialDay', () => dispatch(partialUnscheduleFromLayoutAction(payload, t)));
  };

  const isRemoveValid = confirmText.trim() === confirmWord;
  const isClassSubmitDisabled =
    isBusy ||
    selectedClassIds.length === 0 ||
    !classAcknowledged ||
    (classDayScope === 'selected' && classDayKeys.length === 0);
  const isDaySubmitDisabled = isBusy || clearDayKeys.length === 0 || !dayAcknowledged;

  return (
    <Modal
      show
      onHide={handleClose}
      centered
      size="lg"
      backdrop={pendingAction ? 'static' : true}
      keyboard={!pendingAction}
    >
      <Modal.Header closeButton={!pendingAction}>
        <Modal.Title>{t(prefix + 'scheduleSettingsTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {updatingSchedule && (
          <Alert variant="warning">{t(prefix + 'scheduleSettingsUpdatingWarning')}</Alert>
        )}

        <section className="border rounded p-3 mb-3">
          <h5>{t(prefix + 'scheduleSettingsRemoveAllTitle')}</h5>
          <p className="text-muted mb-3">{t(prefix + 'scheduleSettingsRemoveAllBody')}</p>
          <Form.Group controlId="schedule-remove-confirm">
            <Form.Label>{t(prefix + 'scheduleRemoveConfirmHint', { word: confirmWord })}</Form.Label>
            <Form.Control
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              disabled={isBusy}
            />
          </Form.Group>
          <Button
            variant="outline-danger"
            className="mt-3"
            disabled={isBusy || !isRemoveValid}
            onClick={handleRemoveAll}
          >
            {pendingAction === 'removeAll' && (
              <Spinner animation="border" size="sm" className="me-2" />
            )}
            {t(prefix + 'scheduleSettingsRemoveAllButton')}
          </Button>
        </section>

        <Alert variant="warning">{t(prefix + 'scheduleSettingsCollateralWarning')}</Alert>

        <section className="border rounded p-3 mb-3">
          <h5>{t(prefix + 'scheduleSettingsUnscheduleClassesTitle')}</h5>
          <p className="text-muted mb-3">
            {t(prefix + 'scheduleSettingsUnscheduleClassesBody')}
          </p>
          <div className="mb-3">
            {classOptions.map((tournamentClass) => (
              <Form.Check
                key={tournamentClass.id}
                type="checkbox"
                id={`schedule-settings-class-${tournamentClass.id}`}
                label={tournamentClass.name}
                checked={selectedClassIds.includes(tournamentClass.id)}
                disabled={isBusy}
                onChange={() => toggleClassId(tournamentClass.id)}
              />
            ))}
          </div>
          <Form.Check
            type="radio"
            id="schedule-settings-class-days-all"
            name="schedule-settings-class-day-scope"
            label={t(prefix + 'scheduleSettingsAllDays')}
            checked={classDayScope === 'all'}
            disabled={isBusy}
            onChange={() => handleClassDayScopeChange('all')}
          />
          <Form.Check
            type="radio"
            id="schedule-settings-class-days-selected"
            name="schedule-settings-class-day-scope"
            label={t(prefix + 'scheduleSettingsSelectedDays')}
            checked={classDayScope === 'selected'}
            disabled={isBusy}
            onChange={() => handleClassDayScopeChange('selected')}
          />
          {classDayScope === 'selected' && (
            <div className="mt-2">
              {dayOptions.map((day) => (
                <Form.Check
                  key={day.dayKey}
                  type="checkbox"
                  id={`schedule-settings-class-day-${day.dayKey}`}
                  label={t(prefix + 'scheduleSettingsDayLabel', { day: day.dayKey + 1 })}
                  checked={classDayKeys.includes(day.dayKey)}
                  disabled={isBusy}
                  onChange={() => toggleClassDayKey(day.dayKey)}
                />
              ))}
            </div>
          )}
          <Form.Check
            className="mt-3"
            type="checkbox"
            id="schedule-settings-class-include-started-finished"
            label={t(prefix + 'scheduleSettingsIncludeStartedFinished')}
            checked={classIncludeStartedAndFinished}
            disabled={isBusy}
            onChange={(event) => {
              setClassIncludeStartedAndFinished(event.target.checked);
              setClassAcknowledged(false);
            }}
          />
          <Form.Text className="text-muted d-block">
            {t(prefix + 'scheduleSettingsIncludeStartedFinishedHelp')}
          </Form.Text>
          <Form.Check
            className="mt-3"
            type="checkbox"
            id="schedule-settings-class-ack"
            label={t(prefix + 'scheduleSettingsAcknowledge')}
            checked={classAcknowledged}
            disabled={isBusy}
            onChange={(event) => setClassAcknowledged(event.target.checked)}
          />
          <Button
            variant="outline-danger"
            className="mt-3"
            disabled={isClassSubmitDisabled}
            onClick={handleUnscheduleClasses}
          >
            {pendingAction === 'partialClass' && (
              <Spinner animation="border" size="sm" className="me-2" />
            )}
            {t(prefix + 'scheduleSettingsUnscheduleButton')}
          </Button>
        </section>

        <section className="border rounded p-3">
          <h5>{t(prefix + 'scheduleSettingsClearDaysTitle')}</h5>
          <p className="text-muted mb-3">{t(prefix + 'scheduleSettingsClearDaysBody')}</p>
          {dayOptions.map((day) => (
            <Form.Check
              key={day.dayKey}
              type="checkbox"
              id={`schedule-settings-clear-day-${day.dayKey}`}
              label={t(prefix + 'scheduleSettingsDayLabel', { day: day.dayKey + 1 })}
              checked={clearDayKeys.includes(day.dayKey)}
              disabled={isBusy}
              onChange={() => toggleClearDayKey(day.dayKey)}
            />
          ))}
          <Form.Check
            className="mt-3"
            type="checkbox"
            id="schedule-settings-day-include-started-finished"
            label={t(prefix + 'scheduleSettingsIncludeStartedFinished')}
            checked={dayIncludeStartedAndFinished}
            disabled={isBusy}
            onChange={(event) => {
              setDayIncludeStartedAndFinished(event.target.checked);
              setDayAcknowledged(false);
            }}
          />
          <Form.Text className="text-muted d-block">
            {t(prefix + 'scheduleSettingsIncludeStartedFinishedHelp')}
          </Form.Text>
          <Form.Check
            className="mt-3"
            type="checkbox"
            id="schedule-settings-day-ack"
            label={t(prefix + 'scheduleSettingsAcknowledge')}
            checked={dayAcknowledged}
            disabled={isBusy}
            onChange={(event) => setDayAcknowledged(event.target.checked)}
          />
          <Button
            variant="outline-danger"
            className="mt-3"
            disabled={isDaySubmitDisabled}
            onClick={handleClearDays}
          >
            {pendingAction === 'partialDay' && (
              <Spinner animation="border" size="sm" className="me-2" />
            )}
            {t(prefix + 'scheduleSettingsClearDaysButton')}
          </Button>
        </section>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={Boolean(pendingAction)} onClick={handleClose}>
          {t('modal-cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
